import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTable } from 'react-table';
import moment from 'moment-timezone';
import semver from 'semver';
import { Badge, Table, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { authenticatedFetch } from "./authenticatedFetch";
import config from './config';

const fetchData = async (url) => {
    try {
        const response = await authenticatedFetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching data:', error);
        return null;
    }
};

const abbreviateVersion = (version) => {
    if (version.length > 15) {
        return `${version.slice(0, 8)}...${version.slice(-6)}`;
    }
    return version;
};

const isSemver = (version) => {
    return semver.valid(semver.coerce(version)) !== null;
};

const Lambdas = () => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [lastRefreshed, setLastRefreshed] = useState(moment());
    const [nextRefresh, setNextRefresh] = useState(moment().add(5, 'minutes'));
    const [selectedVersions, setSelectedVersions] = useState([]);

    const envOrder = ['dev', 'uat', 'xbx', 'prd'];

    function processLambdaName(str) {
        const regex = /^...-use1-monitapp/;
        return regex.test(str) ? str.slice(18) : '';
    }

    const fetchDataFromAllEnvironments = async () => {
        setLoading(true);

        const allData = {};

        for (const env of envOrder) {
            const lambdaData = await fetchData(config.apiUrl + `/lambdas/${env}`);
            if (lambdaData) {
                allData[env] = lambdaData.reduce((acc, lambda) => {
                    const lambdaName = processLambdaName(lambda.function_name)
                    if (lambdaName !== '') {
                        acc[lambdaName] = lambda.version;
                    }
                    return acc;
                }, {});
            }
        }

        setData(allData);
        setLastRefreshed(moment());
        setNextRefresh(moment().add(5, 'minutes'));
        setLoading(false);
    };

    useEffect(() => {
        fetchDataFromAllEnvironments();
        const interval = setInterval(fetchDataFromAllEnvironments, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const handleVersionSelect = (version, env, functionName) => {
        if (!isSemver(version)) return;

        setSelectedVersions(prev => {
            const existingIndex = prev.findIndex(v => v.version === version && v.env === env && v.functionName === functionName);
            if (existingIndex !== -1) {
                return prev.filter((_, index) => index !== existingIndex);
            } else if (prev.length === 2) {
                return [prev[1], {version, env, functionName}];
            } else {
                return [...prev, {version, env, functionName}];
            }
        });
    };

    const clearSelection = () => {
        setSelectedVersions([]);
    };

    const columns = useMemo(
        () => {
            const envColumns = envOrder.map(env => ({
                Header: env.toUpperCase(),
                accessor: env,
                width: `${100 / (envOrder.length + 1)}%`,
                Cell: ({value, row}) => {
                    if (!value) return <span>-</span>;

                    const isSelected = selectedVersions.some(v => v.version === value && v.env === env && v.functionName === row.original.functionName);
                    const isSelectable = isSemver(value);

                    return (
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                    <strong>Version:</strong> {value}<br/>
                                    {isSelectable && <strong>Ctrl+Click (Cmd+Click on Mac)
                                        to {isSelected ? 'deselect' : 'select'} for comparison</strong>}
                                </Tooltip>
                            }
                        >
                            <Badge
                                bg={isSelected ? 'info' : 'primary'}
                                style={{
                                    cursor: 'pointer',
                                    opacity: isSelectable ? 1 : 0.6
                                }}
                                onClick={(e) => {
                                    if (e.ctrlKey || e.metaKey) {
                                        handleVersionSelect(value, env, row.original.functionName);
                                    }
                                }}
                            >
                                {abbreviateVersion(value)}
                            </Badge>
                        </OverlayTrigger>
                    );
                },
            }));

            return [
                {
                    Header: 'Function Name',
                    accessor: 'functionName',
                    width: `${100 / (envOrder.length + 1)}%`,
                    Cell: ({value}) => <div style={{whiteSpace: 'nowrap'}}>{value}</div>,
                },
                ...envColumns
            ];
        },
        [data, selectedVersions]
    );

    const tableData = useMemo(() => {
        const allFunctionNames = new Set();
        Object.values(data).forEach(envData => {
            Object.keys(envData).forEach(functionName => allFunctionNames.add(functionName));
        });

        return Array.from(allFunctionNames).map(functionName => ({
            functionName,
            ...Object.fromEntries(envOrder.map(env => [env, data[env]?.[functionName] || null]))
        }));
    }, [data]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({columns, data: tableData});

    useEffect(() => {
        const timer = setInterval(() => {
            setLastRefreshed(moment(lastRefreshed));
            setNextRefresh(moment(nextRefresh));
        }, 1000);
        return () => clearInterval(timer);
    }, [lastRefreshed, nextRefresh]);

    return (
        <div className="container-fluid mt-1">
            <h3>lambda functions</h3>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Button variant="primary" onClick={fetchDataFromAllEnvironments}>Refresh</Button>
                {lastRefreshed && (
                    <div>
                        Last Refresh: {lastRefreshed.fromNow()} | Next: {nextRefresh.fromNow()}
                    </div>
                )}
            </div>
            {selectedVersions.length > 0 && (
                <div className="mb-3">
                    <h5>Selected Versions:</h5>
                    {selectedVersions.map((v, i) => (
                        <Badge key={i} bg="info" className="me-2">
                            {v.functionName} - {v.env}: {abbreviateVersion(v.version)}
                        </Badge>
                    ))}
                    <Button variant="secondary" size="sm" onClick={clearSelection} className="mt-2">
                        Clear Selection
                    </Button>
                </div>
            )}
            {loading ? (
                <div className="spinner-grow text-secondary" role="status">
                    <span className="sr-only"></span>
                </div>
            ) : (
                <div className="table-responsive">
                    <Table striped bordered hover size="sm" {...getTableProps()} style={{tableLayout: 'fixed'}}>
                        <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps()} style={{width: column.width}}>
                                        {column.render('Header')}
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                        {rows.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()} style={{width: cell.column.width}}>
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default Lambdas;